import React from "react";
import { useTranslation } from "react-i18next";
import { H2 } from "@/shared/ui";

export const PrivacyPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-nieve py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <H2 className="mb-8">{t("legal.privacy.title")}</H2>

        <div className="bg-white rounded-lg shadow-lg p-8 prose max-w-none">
          <p className="text-titan mb-6">{t("legal.privacy.lastUpdated")}</p>

          <p className="mb-4">{t("legal.privacy.intro")}</p>

          {/* Section 1: Information Collected */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.privacy.sections.informationCollected.title")}
          </h3>

          <h4 className="text-lg font-semibold mb-2">
            {t("legal.privacy.sections.informationCollected.directInfo.title")}
          </h4>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>
                {t(
                  "legal.privacy.sections.informationCollected.directInfo.items.accountData"
                )}
              </strong>{" "}
              {t(
                "legal.privacy.sections.informationCollected.directInfo.items.accountDataContent"
              )}
            </li>
            <li>
              <strong>
                {t(
                  "legal.privacy.sections.informationCollected.directInfo.items.profileData"
                )}
              </strong>{" "}
              {t(
                "legal.privacy.sections.informationCollected.directInfo.items.profileDataContent"
              )}
            </li>
            <li>
              <strong>
                {t(
                  "legal.privacy.sections.informationCollected.directInfo.items.documentContent"
                )}
              </strong>{" "}
              {t(
                "legal.privacy.sections.informationCollected.directInfo.items.documentContentContent"
              )}
            </li>
            <li>
              <strong>
                {t(
                  "legal.privacy.sections.informationCollected.directInfo.items.paymentInfo"
                )}
              </strong>{" "}
              {t(
                "legal.privacy.sections.informationCollected.directInfo.items.paymentInfoContent"
              )}
            </li>
          </ul>

          <h4 className="text-lg font-semibold mb-2">
            {t("legal.privacy.sections.informationCollected.autoInfo.title")}
          </h4>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>
                {t(
                  "legal.privacy.sections.informationCollected.autoInfo.items.usageData"
                )}
              </strong>{" "}
              {t(
                "legal.privacy.sections.informationCollected.autoInfo.items.usageDataContent"
              )}
            </li>
            <li>
              <strong>
                {t(
                  "legal.privacy.sections.informationCollected.autoInfo.items.technicalData"
                )}
              </strong>{" "}
              {t(
                "legal.privacy.sections.informationCollected.autoInfo.items.technicalDataContent"
              )}
            </li>
            <li>
              <strong>
                {t(
                  "legal.privacy.sections.informationCollected.autoInfo.items.cookies"
                )}
              </strong>{" "}
              {t(
                "legal.privacy.sections.informationCollected.autoInfo.items.cookiesContent"
              )}
            </li>
          </ul>

          {/* Section 2: How We Use Info */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.privacy.sections.howWeUse.title")}
          </h3>
          <p className="mb-4">{t("legal.privacy.sections.howWeUse.intro")}</p>
          <ul className="list-disc pl-6 mb-4">
            <li>{t("legal.privacy.sections.howWeUse.purposes.provide")}</li>
            <li>{t("legal.privacy.sections.howWeUse.purposes.generate")}</li>
            <li>{t("legal.privacy.sections.howWeUse.purposes.manage")}</li>
            <li>{t("legal.privacy.sections.howWeUse.purposes.payments")}</li>
            <li>
              {t("legal.privacy.sections.howWeUse.purposes.notifications")}
            </li>
            <li>{t("legal.privacy.sections.howWeUse.purposes.improve")}</li>
            <li>{t("legal.privacy.sections.howWeUse.purposes.prevent")}</li>
            <li>{t("legal.privacy.sections.howWeUse.purposes.comply")}</li>
          </ul>

          {/* Section 3: Legal Basis */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.privacy.sections.legalBasis.title")}
          </h3>
          <p className="mb-4">{t("legal.privacy.sections.legalBasis.intro")}</p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>
                {t("legal.privacy.sections.legalBasis.bases.contract")}
              </strong>{" "}
              {t("legal.privacy.sections.legalBasis.bases.contractContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.legalBasis.bases.consent")}
              </strong>{" "}
              {t("legal.privacy.sections.legalBasis.bases.consentContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.legalBasis.bases.legitimate")}
              </strong>{" "}
              {t("legal.privacy.sections.legalBasis.bases.legitimateContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.legalBasis.bases.legal")}
              </strong>{" "}
              {t("legal.privacy.sections.legalBasis.bases.legalContent")}
            </li>
          </ul>

          {/* Section 4: Sharing Information */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.privacy.sections.sharingInfo.title")}
          </h3>
          <p className="mb-4">
            {t("legal.privacy.sections.sharingInfo.intro")}
          </p>

          <h4 className="text-lg font-semibold mb-2">
            {t("legal.privacy.sections.sharingInfo.providers.title")}
          </h4>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>
                {t("legal.privacy.sections.sharingInfo.providers.supabase")}
              </strong>{" "}
              {t(
                "legal.privacy.sections.sharingInfo.providers.supabaseContent"
              )}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.sharingInfo.providers.openai")}
              </strong>{" "}
              {t("legal.privacy.sections.sharingInfo.providers.openaiContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.sharingInfo.providers.stripe")}
              </strong>{" "}
              {t("legal.privacy.sections.sharingInfo.providers.stripeContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.sharingInfo.providers.hosting")}
              </strong>{" "}
              {t("legal.privacy.sections.sharingInfo.providers.hostingContent")}
            </li>
          </ul>

          <h4 className="text-lg font-semibold mb-2">
            {t("legal.privacy.sections.sharingInfo.legal.title")}
          </h4>
          <p className="mb-4">
            {t("legal.privacy.sections.sharingInfo.legal.content")}
          </p>

          {/* Section 5: Storage and Security */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.privacy.sections.storageSecurity.title")}
          </h3>

          <h4 className="text-lg font-semibold mb-2">
            {t("legal.privacy.sections.storageSecurity.location.title")}
          </h4>
          <p className="mb-4">
            {t("legal.privacy.sections.storageSecurity.location.content")}
          </p>

          <h4 className="text-lg font-semibold mb-2">
            {t("legal.privacy.sections.storageSecurity.measures.title")}
          </h4>
          <ul className="list-disc pl-6 mb-4">
            <li>
              {t(
                "legal.privacy.sections.storageSecurity.measures.items.transit"
              )}
            </li>
            <li>
              {t("legal.privacy.sections.storageSecurity.measures.items.rest")}
            </li>
            <li>
              {t(
                "legal.privacy.sections.storageSecurity.measures.items.passwords"
              )}
            </li>
            <li>
              {t("legal.privacy.sections.storageSecurity.measures.items.rls")}
            </li>
            <li>
              {t(
                "legal.privacy.sections.storageSecurity.measures.items.audits"
              )}
            </li>
            <li>
              {t(
                "legal.privacy.sections.storageSecurity.measures.items.access"
              )}
            </li>
          </ul>

          {/* Section 6: Data Retention */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.privacy.sections.dataRetention.title")}
          </h3>
          <p className="mb-4">
            {t("legal.privacy.sections.dataRetention.intro")}
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              {t("legal.privacy.sections.dataRetention.conditions.active")}
            </li>
            <li>
              {t("legal.privacy.sections.dataRetention.conditions.necessary")}
            </li>
            <li>
              {t("legal.privacy.sections.dataRetention.conditions.required")}
            </li>
          </ul>
          <p className="mb-4">
            {t("legal.privacy.sections.dataRetention.deletion")}
          </p>

          {/* Section 7: Your Rights */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.privacy.sections.yourRights.title")}
          </h3>
          <p className="mb-4">{t("legal.privacy.sections.yourRights.intro")}</p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>
                {t("legal.privacy.sections.yourRights.rights.access")}
              </strong>{" "}
              {t("legal.privacy.sections.yourRights.rights.accessContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.yourRights.rights.correction")}
              </strong>{" "}
              {t("legal.privacy.sections.yourRights.rights.correctionContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.yourRights.rights.deletion")}
              </strong>{" "}
              {t("legal.privacy.sections.yourRights.rights.deletionContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.yourRights.rights.portability")}
              </strong>{" "}
              {t("legal.privacy.sections.yourRights.rights.portabilityContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.yourRights.rights.objection")}
              </strong>{" "}
              {t("legal.privacy.sections.yourRights.rights.objectionContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.yourRights.rights.restriction")}
              </strong>{" "}
              {t("legal.privacy.sections.yourRights.rights.restrictionContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.yourRights.rights.withdraw")}
              </strong>{" "}
              {t("legal.privacy.sections.yourRights.rights.withdrawContent")}
            </li>
          </ul>
          <p className="mb-4">
            {t("legal.privacy.sections.yourRights.contact")}{" "}
            <a
              href="mailto:nolimstudios@gmail.com"
              className="text-tiber-500 hover:underline"
            >
              Nolim Studios Support
            </a>
          </p>

          {/* Section 8: Cookies */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.privacy.sections.cookies.title")}
          </h3>
          <p className="mb-4">{t("legal.privacy.sections.cookies.intro")}</p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              <strong>
                {t("legal.privacy.sections.cookies.types.essential")}
              </strong>{" "}
              {t("legal.privacy.sections.cookies.types.essentialContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.cookies.types.functional")}
              </strong>{" "}
              {t("legal.privacy.sections.cookies.types.functionalContent")}
            </li>
            <li>
              <strong>
                {t("legal.privacy.sections.cookies.types.analytics")}
              </strong>{" "}
              {t("legal.privacy.sections.cookies.types.analyticsContent")}
            </li>
          </ul>
          <p className="mb-4">
            {t("legal.privacy.sections.cookies.configuration")}
          </p>

          {/* Section 9: Minors */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.privacy.sections.minors.title")}
          </h3>
          <p className="mb-4">{t("legal.privacy.sections.minors.content")}</p>

          {/* Section 10: International Transfers */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.privacy.sections.internationalTransfers.title")}
          </h3>
          <p className="mb-4">
            {t("legal.privacy.sections.internationalTransfers.content")}
          </p>

          {/* Section 11: Policy Updates */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.privacy.sections.policyUpdates.title")}
          </h3>
          <p className="mb-4">
            {t("legal.privacy.sections.policyUpdates.content")}
          </p>

          {/* Section 12: Contact */}
          <h3 className="text-xl font-semibold mb-4">
            {t("legal.privacy.sections.contact.title")}
          </h3>
          <p className="mb-4">{t("legal.privacy.sections.contact.intro")}</p>
          <p className="mb-4">
            <strong>{t("legal.privacy.sections.contact.privacyEmail")}</strong>{" "}
            <a
              href="mailto:nolimstudios@gmail.com"
              className="text-tiber-500 hover:underline"
            >
              Nolim Studios Support
            </a>
          </p>
          <p className="mb-4">
            <strong>{t("legal.privacy.sections.contact.generalEmail")}</strong>{" "}
            <a
              href="mailto:nolimstudios@gmail.com"
              className="text-tiber-500 hover:underline"
            >
              Nolim Studios Support
            </a>
          </p>
          <p className="mb-4">
            <strong>{t("legal.privacy.sections.contact.company")}</strong>
            <br />
            {t("legal.privacy.sections.contact.companySubtitle")}
          </p>

          <div className="mt-8 p-4 bg-tiber-100 rounded-lg">
            <p className="text-sm text-titan">
              {t("legal.privacy.sections.acknowledgment")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
